/**
 * Barber create/edit page
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useBarber } from '@/contexts/BarberContext';
import { useAuth } from '@/contexts/AuthContext';
import { Barber } from '@/types';
import { BarberForm, CreateBarberWithCredentials } from '@/components/barbers/BarberForm';
import { LoadingState } from '@/components/shared/LoadingState';
import { toast } from '@/lib/toast';

export default function BarberFormPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const franchiseId = searchParams.get('franchiseId');
  const branchId = searchParams.get('branchId');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getBarber, updateBarber } = useBarber();
  const [barber, setBarber] = useState<Barber | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadBarber();
    }
  }, [id]);

  const loadBarber = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getBarber(id);
      setBarber(data);
    } catch (error) {
      console.error('Error loading barber:', error);
      navigate('/barbers');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data: any) => {
    setSubmitting(true);
    try {
      if (id && barber) {
        // Editar barbero existente
        await updateBarber(id, data);
        toast.success('Barbero actualizado correctamente');
      } else {
        // Crear nuevo barbero usando Cloud Function
        const createData = data as CreateBarberWithCredentials;

        const createBarberFn = httpsCallable(functions, 'createBarber');
        const result = await createBarberFn({
          email: createData.email,
          password: createData.password,
          displayName: createData.displayName,
          franchiseId: createData.franchiseId,
          branchId: createData.branchId,
          specialties: createData.specialties,
          bio: createData.bio,
          photoURL: createData.photoURL,
        });

        const response = result.data as { success: boolean; message: string; barberId?: string };

        if (response.success) {
          toast.success('Barbero creado correctamente');
        } else {
          throw new Error(response.message || 'Error al crear barbero');
        }
      }
      navigate('/barbers');
    } catch (error: any) {
      console.error('Error saving barber:', error);
      toast.error(error.message || 'Error al guardar barbero');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/barbers');
  };

  if ((!franchiseId || !branchId) && !barber) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <p className="text-danger">Franchise ID and Branch ID are required to create a barber.</p>
          <button onClick={() => navigate('/barbers')} className="mt-4 text-info hover:underline">
            Back to Barbers
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingState message="Loading barber..." />;
  }

  return (
    <div className="container mx-auto p-6">
      <BarberForm
        barber={barber || undefined}
        franchiseId={franchiseId || barber?.franchiseId || ''}
        branchId={branchId || barber?.branchId || ''}
        userId={user?.id || ''}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={submitting}
      />
    </div>
  );
}
